import type { InjectionProtocol, ProtocolMarket } from "./types";

/**
 * Which of a medication's procedures apply where she is.
 *
 * Most protocols declare no markets, because most procedures are the same
 * object everywhere: a prefilled syringe is a prefilled syringe in Lyon and in
 * Boston. Those are returned untouched, and this function is then a no-op she
 * never notices.
 *
 * Follitropin alfa's vial is the case that made this necessary. The European
 * multidose is drawn by injecting air into the vial and pulling to the dose in
 * IU; the American one by overshooting the mark and pushing back to it. The
 * European product is never refrigerated; the American one waits thirty minutes
 * out of the fridge. Neither is a safe approximation of the other, and the box
 * looks the same.
 *
 * ### An unknown country hides a market-specific walk rather than guessing one
 *
 * Her country is a preference — saved, or inferred from an address or a locale —
 * and it can be stale, because patients travel. `emergencyNumbers` lives with
 * that by printing the country's name beside the digits, so a wrong answer is
 * visible before she dials. A procedure has no such tell: she would simply do
 * the wrong steps.
 *
 * So the rule is asymmetric on purpose. A protocol that names markets is
 * offered only when we know her country and it is one of them. Not offering a
 * walk is a real answer — she still has the medication, its factsheet and her
 * clinic — where offering the wrong country's is not.
 */
export function protocolsForMarket(
  protocols: readonly InjectionProtocol[],
  countryCode: string | null | undefined,
): readonly InjectionProtocol[] {
  const country = normalizeMarket(countryCode);
  return protocols.filter((protocol) => {
    if (!protocol.markets) return true;
    if (!country) return false;
    return protocol.markets.includes(country);
  });
}

/**
 * An ISO 3166-1 alpha-2 code, upper-cased, or `null`.
 *
 * Deliberately not a mapping of anything to `"EU"`. A protocol that applies
 * across the bloc lists its countries, because "is France in the EU" is a
 * question with a stable answer and "is this market code the bloc" is a
 * question about our own data. The one place `"EU"` appears is a brand's market
 * list, where it means "the name on the box across the bloc" and no procedure
 * reads it.
 */
function normalizeMarket(
  countryCode: string | null | undefined,
): ProtocolMarket | null {
  if (!countryCode) return null;
  const trimmed = countryCode.trim().toUpperCase();
  return /^[A-Z]{2}$/u.test(trimmed) ? trimmed : null;
}
